import { useApolloClient } from "@apollo/client";
import { useCallback } from "react";
import type { ItemTemplateSearchFilterPropertyFilterInput } from "../../../clients/graphqlTypes.js";
import type { ItemProductSearchFilterDefinitionsFragment } from "../products/productSearchFilters.generated.js";
import type { ProductSearchFiltersInput } from "./productSearchFilterSlots.js";
import { productSearchFiltersToInput } from "./productSearchFilterSlots.js";
import {
  isComputedSearchFilterSlot,
  searchFilterSlotHasValue,
  searchFilterSlotToInput,
} from "./searchFilterSlots.js";
import { useProductSearchFiltersSection_DefineProductSearchFilterDefinitionsMutation } from "./useProductSearchFilterDefinitionMutations.generated.js";

export function useDefineItemProductSearchFilterDefinitionsMutation(
  refetchQueries: string[] | undefined
) {
  const client = useApolloClient();
  const [defineItemProductSearchFilterDefinitions, query] =
    useProductSearchFiltersSection_DefineProductSearchFilterDefinitionsMutation(
      {
        client,
        refetchQueries,
      }
    );

  return [defineItemProductSearchFilterDefinitions, query] as const;
}

/**
 * One merged mutation now carries a placeholder's whole product-filter set —
 * authored and expression-driven together — so every write re-sends the
 * filters it does not touch.
 */
export function useProductSearchFilterMutations({
  projectId,
  docId,
  itemId,
  definitions,
  refetchQueries,
}: {
  projectId: string | null;
  docId: string;
  itemId: string;
  definitions: ItemProductSearchFilterDefinitionsFragment;
  refetchQueries?: string[];
}) {
  const [defineProductSearchFilters] =
    useDefineItemProductSearchFilterDefinitionsMutation(refetchQueries);

  const writeFilters = useCallback(
    async (patch: Partial<ProductSearchFiltersInput>) => {
      await defineProductSearchFilters({
        variables: {
          input: {
            projectId,
            docId,
            itemId,
            ...productSearchFiltersToInput(definitions),
            ...patch,
          },
        },
      });
    },
    [defineProductSearchFilters, definitions, docId, itemId, projectId]
  );

  /** Replace one property filter in place, so editing does not reorder rows. */
  const setPropertyFilterSlot = useCallback(
    async (
      propertyKey: string,
      entry: ItemTemplateSearchFilterPropertyFilterInput
    ) => {
      const entries = definitions.propertyFilters.map(searchFilterSlotToInput);
      const index = definitions.propertyFilters.findIndex(
        f => f.key === propertyKey
      );

      await writeFilters({
        propertyFilters:
          index === -1
            ? [...entries, entry]
            : entries.map((existing, i) => (i === index ? entry : existing)),
      });
    },
    [definitions.propertyFilters, writeFilters]
  );

  const deletePropertyFilterSlot = useCallback(
    async (propertyKey: string) => {
      await writeFilters({
        propertyFilters: definitions.propertyFilters
          .filter(f => f.key !== propertyKey)
          .map(searchFilterSlotToInput),
      });
    },
    [definitions.propertyFilters, writeFilters]
  );

  /**
   * The authored filter form owns only the rows it can represent; the rest —
   * expression-driven rows, and authored rows left valueless by an expression
   * deletion — are preserved around it.
   */
  const defineAuthoredPropertyFilterSlots = useCallback(
    async (authored: ItemTemplateSearchFilterPropertyFilterInput[]) => {
      await writeFilters({
        propertyFilters: [
          ...authored,
          ...definitions.propertyFilters
            .filter(
              slot =>
                isComputedSearchFilterSlot(slot) ||
                !searchFilterSlotHasValue(slot)
            )
            .map(searchFilterSlotToInput),
        ],
      });
    },
    [definitions.propertyFilters, writeFilters]
  );

  return {
    writeFilters,
    setPropertyFilterSlot,
    deletePropertyFilterSlot,
    defineAuthoredPropertyFilterSlots,
  };
}
